import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatError } from "@/utils/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Stethoscope, Eye, EyeOff, Chrome } from "lucide-react";
import Logo from "@/assets/Logo.webp";

export default function Login() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [registerForm, setRegisterForm] = useState({ name: "", email: "", password: "", role: "patient" });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/login", loginForm);
      setUser(data.data.user);
      navigate("/dashboard");
    } catch (err) {
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/register", registerForm);
      setUser(data.data.user);
      navigate("/dashboard");
    } catch (err) {
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex">
      {/* Left - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/ea411ceb-1216-4730-a8c5-f7c6758a3a45/images/de11506316359c69780ba7d3cc9457323d0578b0e04a54ad290b8f0bf39e4c4d.png"
          alt="Healthcare Background"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-emerald-900/60"></div>
        <div className="relative z-10 flex flex-col justify-between p-10 w-full">
          <div className="flex items-center gap-3">
            <img
              src={Logo}
              alt="Logo"
              className="w-12 h-12 rounded-xl"
            />
            <div>
              <p className="text-white font-bold text-xl font-sans leading-none">MedLif</p>
              <p className="text-emerald-200 text-xs mt-0.5">Clinic Management System</p>
            </div>
          </div>
          <div>
            <h1 className="text-4xl sm:text-5xl font-bold text-white font-sans leading-tight mb-4">
              Modern Healthcare<br />Management
            </h1>
            <p className="text-emerald-100 text-lg leading-relaxed max-w-md">
              Streamline your clinic operations. Manage doctors, patients, appointments, lab tests, and billing all in one place.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-4">
              {[
                { label: "Patients Managed", value: "10K+" },
                { label: "Doctors", value: "500+" },
                { label: "Clinics", value: "50+" },
              ].map((stat) => (
                <div key={stat.label} className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                  <p className="text-white text-2xl font-bold font-sans">{stat.value}</p>
                  <p className="text-emerald-200 text-xs mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Right - Login Form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-slate-50">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="flex items-center gap-3 mb-8 lg:hidden">
            <div className="w-10 h-10 bg-emerald-700 rounded-xl flex items-center justify-center">
              <img
                src={Logo}
                alt="Logo"
                className="w-12 h-12 rounded-xl"
              />
            </div>
            <div>
              <p className="font-bold text-slate-900 font-sans">MedLif</p>
              <p className="text-xs text-slate-500">Clinic Management System</p>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
            <h2 className="text-2xl font-bold text-slate-900 font-sans mb-1">Welcome back</h2>
            <p className="text-slate-500 text-sm mb-6">Sign in to your clinic dashboard</p>

            {error && (
              <div className="bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 rounded-lg text-sm mb-5" data-testid="login-error">
                {error}
              </div>
            )}

            <Tabs defaultValue="login">
              <TabsList className="w-full mb-6 bg-slate-100">
                <TabsTrigger value="login" className="flex-1" data-testid="login-tab">Sign In</TabsTrigger>
                <TabsTrigger value="register" className="flex-1" data-testid="register-tab">Register</TabsTrigger>
              </TabsList>

              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <Label htmlFor="login-email" className="text-slate-700 text-sm font-medium">Email</Label>
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="admin@clinic.com"
                      value={loginForm.email}
                      onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                      className="mt-1.5 border-slate-200 focus:ring-emerald-500/50"
                      required
                      data-testid="login-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="login-password" className="text-slate-700 text-sm font-medium">Password</Label>
                    <div className="relative mt-1.5">
                      <Input
                        id="login-password"
                        type={showPass ? "text" : "password"}
                        placeholder="••••••••"
                        value={loginForm.password}
                        onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                        className="border-slate-200 pr-10 focus:ring-emerald-500/50"
                        required
                        data-testid="login-password-input"
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                        onClick={() => setShowPass(!showPass)}
                      >
                        {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-emerald-700 hover:bg-emerald-800 text-white h-11"
                    disabled={loading}
                    data-testid="login-submit-btn"
                  >
                    {loading ? "Signing in..." : "Sign In"}
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div>
                    <Label className="text-slate-700 text-sm font-medium">Full Name</Label>
                    <Input
                      type="text"
                      placeholder="John Doe"
                      value={registerForm.name}
                      onChange={(e) => setRegisterForm({ ...registerForm, name: e.target.value })}
                      className="mt-1.5 border-slate-200"
                      required
                      data-testid="register-name-input"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-700 text-sm font-medium">Email</Label>
                    <Input
                      type="email"
                      placeholder="john@example.com"
                      value={registerForm.email}
                      onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                      className="mt-1.5 border-slate-200"
                      required
                      data-testid="register-email-input"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-700 text-sm font-medium">Password</Label>
                    <Input
                      type="password"
                      placeholder="••••••••"
                      value={registerForm.password}
                      onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                      className="mt-1.5 border-slate-200"
                      required
                      data-testid="register-password-input"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-700 text-sm font-medium">Register as</Label>
                    <select
                      value={registerForm.role}
                      onChange={(e) => setRegisterForm({ ...registerForm, role: e.target.value })}
                      className="mt-1.5 w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                      data-testid="register-role-select"
                    >
                      <option value="patient">Patient</option>
                      <option value="doctor">Doctor</option>
                      <option value="receptionist">Receptionist</option>
                      <option value="lab_technician">Lab Technician</option>
                      <option value="staff">Staff</option>
                    </select>
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-emerald-700 hover:bg-emerald-800 text-white h-11"
                    disabled={loading}
                    data-testid="register-submit-btn"
                  >
                    {loading ? "Creating account..." : "Create Account"}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200" />
              </div>
              <div className="relative flex justify-center text-xs text-slate-500 bg-white px-3">
                OR CONTINUE WITH
              </div>
            </div>

            <Button
              variant="outline"
              className="w-full border-slate-200 text-slate-700 hover:bg-slate-50 h-11 gap-2"
              onClick={handleGoogleLogin}
              data-testid="google-login-btn"
            >
              <Chrome size={18} />
              Continue with Google
            </Button>

            <p className="mt-6 text-center text-xs text-slate-500">
              Admin credentials: admin@clinic.com / Admin@123
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
